import * as Yup from 'yup'
import { ManifestIntegration } from 'models'
import { TICKETING_SYSTEMS_PATH } from '../integrationPaths'

/* Ticketing Systems, where every exit from the setup page lands. */
export const APPLICATIONS_PATH = TICKETING_SYSTEMS_PATH

/* The address a fresh setup starts from, and where the callback page lands once the approval is
 * completed. */
export const freshStartPath = (slug: string) => `${APPLICATIONS_PATH}/${slug}/add`

/* The same page carrying the instance, so a reload resumes instead of starting over. */
export const instanceScopedPath = (slug: string, applicationId: string) =>
  `${APPLICATIONS_PATH}/${slug}/${applicationId}/add`

export const APPLICATION_STEPS = ['Connect', 'Configure instance']

export const CONNECTION_DETAILS_TITLE = 'Connection Details'

export const CONNECTION_DETAILS_DESCRIPTION =
  'Review what Onyxia will ask for, then approve the connection at your provider'

export const CONFIGURE_DETAILS_DESCRIPTION = 'Name this instance and save to finish the setup'

export const AUTH_METHOD_TITLE = 'Authentication method'

export const REQUESTED_PERMISSIONS_TITLE = 'Permissions Onyxia will request'

export const CONNECTED_BANNER_TITLE = 'Connection successful'

export const CONNECTED_BANNER_TEXT = 'Onyxia is connected and the permissions below were granted.'

export const NAME_REQUIRED_MESSAGE = 'Please enter a name for the instance'

export const NAME_TAKEN_MESSAGE = 'An instance with this name already exists'

export const CONNECT_FAILED_MESSAGE = 'The connection could not be started. Please try again.'

export const RETRY_FAILED_MESSAGE = 'The approval could not be restarted. Please try again.'

export const REMOVE_FAILED_MESSAGE = 'The instance could not be removed'

export const APPROVAL_UNFINISHED_TITLE = 'The approval was not finished'

export const APPROVAL_UNFINISHED_MESSAGE =
  'This instance is still waiting for its approval. Try again to pick up where you left off.'

export const APPROVAL_FAILED_TITLE = 'The approval did not go through'

export const AMBIGUOUS_SITES_TITLE = 'Your approval reached these sites:'

export const TRY_AGAIN_LABEL = 'Try again'

export const SAVE_FAILED_MESSAGE = 'The instance could not be saved. Please try again.'

export const INSTANCE_GONE_MESSAGE = 'That integration instance no longer exists'

export const INSUFFICIENT_PERMISSIONS = 'Insufficient permissions'

export interface ApplicationFormValues {
  name: string
  description: string
}

export interface ConfigureFormValues {
  name: string
}

export const applicationFormDefaults: ApplicationFormValues = { name: '', description: '' }

/* Exact, case-sensitive, matching how the backend compares display names, so the page never
 * accepts a name the create route would refuse. */
const nameField = (takenNames: string[]) =>
  Yup.string()
    .required(NAME_REQUIRED_MESSAGE)
    .test('name-available', NAME_TAKEN_MESSAGE, (value) => !value || !takenNames.includes(value))

export const applicationFormSchema = (takenNames: string[]) =>
  Yup.object({
    name: nameField(takenNames),
    description: Yup.string()
  })

export const configureFormSchema = (takenNames: string[]) => Yup.object({ name: nameField(takenNames) })

/* Placeholders until the instructions are written; the numbering is what the shared pane strips. */
export const APPLICATION_INSTRUCTION_PLACEHOLDERS = [
  '1. Sign in to your provider with the account Onyxia should act as.',
  '2. Approve the permissions listed here on the provider consent screen.',
  '3. Return to this page to confirm the connection and finish the setup.'
]

/* The shared instructions pane keys its screenshots off `documentation.path`; a blank path
 * resolves none, which is what the placeholders above want. */
export const PLACEHOLDER_DOCUMENTATION = { documentation: { path: '' } } as ManifestIntegration
